import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Box,
  Grid,
  Autocomplete
} from '@mui/material';
import { WalteroButton, WalteroTypography } from '../waltero';
import { subscriptionService } from '../../services/subscriptionService';
import { API_BASE_URL, API_ENDPOINTS } from '../../config/api';
import axios from 'axios';

const AssignSubscriptionModal = ({ open, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    organizationId: '',
    subscriptionTypeId: '',
    customName: '',
    overridePrice: '',
    overrideDurationDays: '',
    overrideGracePeriodDays: '',
    notes: ''
  });
  const [subscriptionTypes, setSubscriptionTypes] = useState([]);
  const [organizationOptions, setOrganizationOptions] = useState([]);
  const [organizationSearchTerm, setOrganizationSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open]);

  const loadData = async () => {
    try {
      setDataLoading(true);
      setError(null);
      
      const typesResult = await subscriptionService.getSubscriptionTypes();
      setSubscriptionTypes(typesResult.data || []);
      
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load subscription types');
    } finally {
      setDataLoading(false);
    }
  };

  const searchOrganizations = async (searchTerm) => {
    try {
      console.log('🔍 Searching organizations with term:', searchTerm);
      const token = localStorage.getItem('authToken');
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      let response;
      if (!searchTerm || searchTerm.length === 0) {
        // Load all organizations when no search term
        console.log('📋 Loading all organizations');
        response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.ORGANIZATIONS.LIST}`, { headers });
      } else {
        // Use the search endpoint for filtered results
        console.log('🔍 Searching organizations with backend search');
        response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.ORGANIZATIONS.SEARCH}?q=${encodeURIComponent(searchTerm)}`, { headers });
      }
      
      console.log('📋 Organization search response:', response.data);
      const organizations = response.data.data || response.data || [];
      console.log('📋 Setting organization options:', organizations);
      setOrganizationOptions(organizations);
    } catch (error) {
      console.error('Error searching organizations:', error);
      setError('Failed to search organizations');
    }
  };

  const handleChange = (field) => (event, value) => {
    if (field === 'subscriptionTypeId') {
      const typeId = event.target.value;
      const type = subscriptionTypes.find(t => t.id === typeId);
      setSelectedType(type);
      setFormData(prev => ({
        ...prev,
        [field]: typeId
      }));
    } else {
      const newValue = value !== undefined ? value : event.target.value;
      setFormData(prev => ({
        ...prev,
        [field]: newValue
      }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    try {
      setLoading(true);
      setError(null);

      // Validate required fields
      if (!formData.organizationId) {
        throw new Error('Organization is required');
      }
      if (!formData.subscriptionTypeId) {
        throw new Error('Subscription type is required');
      }

      // Prepare overrides
      const overrides = {};
      if (formData.customName.trim()) {
        overrides.customName = formData.customName.trim();
      }
      if (formData.overridePrice) {
        overrides.price = parseFloat(formData.overridePrice);
      }
      if (formData.overrideDurationDays) {
        overrides.durationDays = parseInt(formData.overrideDurationDays);
      }
      if (formData.overrideGracePeriodDays) {
        overrides.gracePeriodDays = Math.max(0, parseInt(formData.overrideGracePeriodDays, 10) || 0);
      }
      if (formData.notes.trim()) {
        overrides.notes = formData.notes.trim();
      }

      // Assign subscription to organization
      await subscriptionService.assignSubscriptionToOrganization(
        formData.organizationId,
        formData.subscriptionTypeId,
        overrides
      );
      
      // Reset form and close modal
      resetForm();
      onSuccess?.();
      onClose();
      
    } catch (err) {
      console.error('Error assigning subscription:', err);
      setError(err.message || 'Failed to assign subscription');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      organizationId: '',
      subscriptionTypeId: '',
      customName: '',
      overridePrice: '',
      overrideDurationDays: '',
      overrideGracePeriodDays: '',
      notes: ''
    });
    setSelectedType(null);
  };

  const handleClose = () => {
    if (!loading) {
      resetForm();
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        <WalteroTypography 
          variant="h5" 
          sx={{ 
            color: 'waltero.primary.coreBlue',
            fontWeight: 600 
          }}
        >
          Assign Subscription to Organization
        </WalteroTypography>
      </DialogTitle>
      
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          
          {dataLoading ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <WalteroTypography>Loading...</WalteroTypography>
            </Box>
          ) : (
            <Grid container spacing={3}>
              {/* Organization Selection */}
              <Grid item xs={12} md={6}>
                <Autocomplete
                  value={organizationOptions.find(org => org.id === formData.organizationId) || null}
                  onChange={(event, newValue) => {
                    setFormData(prev => ({
                      ...prev,
                      organizationId: newValue ? newValue.id : ''
                    }));
                  }}
                  onOpen={() => {
                    // Load all organizations when dropdown opens
                    searchOrganizations('');
                  }}
                  onInputChange={(event, newInputValue) => {
                    setOrganizationSearchTerm(newInputValue);
                    if (newInputValue.length >= 2) {
                      searchOrganizations(newInputValue);
                    } else if (newInputValue.length === 0) {
                      // Load all organizations when search is cleared
                      searchOrganizations('');
                    } else {
                      setOrganizationOptions([]);
                    }
                  }}
                  options={organizationOptions}
                  getOptionLabel={(option) => option.name || option.id}
                  disabled={loading}
                  loading={dataLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Organization *"
                      placeholder="Search for organization..."
                      helperText="Type at least 2 characters to search"
                    />
                  )}
                />
              </Grid>

              {/* Subscription Type Selection */}
              <Grid item xs={12}>
                <FormControl
                  fullWidth
                  disabled={loading}
                  sx={{ minWidth: 280, width: '100%' }}
                >
                  <InputLabel>Subscription Type *</InputLabel>
                  <Select
                    value={formData.subscriptionTypeId}
                    onChange={handleChange('subscriptionTypeId')}
                    label="Subscription Type *"
                    sx={{ width: '100%' }}
                  >
                    {subscriptionTypes.filter(type => type.isActive).map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.name} ({type.durationDays} days)
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Base Subscription Info */}
              {selectedType && (
                <Grid item xs={12}>
                  <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                    <WalteroTypography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Base Subscription Details:
                    </WalteroTypography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <WalteroTypography variant="caption" color="text.secondary">Duration</WalteroTypography>
                        <WalteroTypography variant="body2">{selectedType.durationDays} days</WalteroTypography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <WalteroTypography variant="caption" color="text.secondary">Base Price (per sensor / month)</WalteroTypography>
                        <WalteroTypography variant="body2">
                          {selectedType.basePrice ? `$${selectedType.basePrice} / sensor / month` : 'Not set'}
                        </WalteroTypography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <WalteroTypography variant="caption" color="text.secondary">Grace Period</WalteroTypography>
                        <WalteroTypography variant="body2">
                          {selectedType.gracePeriodDays ? `${selectedType.gracePeriodDays} days` : '0 days'}
                        </WalteroTypography>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
              )}

              {/* Override Fields */}
              <Grid item xs={12}>
                <WalteroTypography variant="h6" sx={{ color: 'waltero.primary.coreBlue', fontWeight: 600, mb: 2 }}>
                  Organization-Specific Overrides
                </WalteroTypography>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Custom Name"
                  value={formData.customName}
                  onChange={handleChange('customName')}
                  disabled={loading}
                  placeholder="Custom display name for this organization"
                  helperText="Leave empty to use the base subscription name"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Override Price"
                  type="number"
                  value={formData.overridePrice}
                  onChange={handleChange('overridePrice')}
                  disabled={loading}
                  inputProps={{ min: 0, step: 0.01 }}
                  placeholder="Custom price for this organization"
                  helperText="Custom price per sensor per month. Leave empty to use base price."
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Override Duration (Days)"
                  type="number"
                  value={formData.overrideDurationDays}
                  onChange={handleChange('overrideDurationDays')}
                  disabled={loading}
                  inputProps={{ min: 1 }}
                  placeholder="Custom duration"
                  helperText="Leave empty to use base duration"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Override Grace Period (Days)"
                  type="number"
                  value={formData.overrideGracePeriodDays}
                  onChange={handleChange('overrideGracePeriodDays')}
                  disabled={loading}
                  inputProps={{ min: 0 }}
                  placeholder="Custom grace period"
                  helperText="Leave empty to use base grace period"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  value={formData.notes}
                  onChange={handleChange('notes')}
                  disabled={loading}
                  multiline
                  rows={2}
                  placeholder="Optional notes (e.g., reason for custom pricing)"
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <WalteroButton 
            onClick={handleClose}
            disabled={loading || dataLoading}
            variant="outlined"
          >
            CANCEL
          </WalteroButton>
          <WalteroButton 
            type="submit"
            disabled={loading || dataLoading}
            variant="contained"
          >
            {loading ? 'ASSIGNING...' : 'ASSIGN SUBSCRIPTION'}
          </WalteroButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AssignSubscriptionModal;
